import SEOHead from "@/components/SEOHead";

const Confidentialite = () => {
  return (
    <div className="min-h-screen bg-background py-16">
      <SEOHead
        title="Politique de confidentialité | Immovox"
        description="Découvrez comment Immovox protège vos données personnelles. Politique RGPD conforme pour agences immobilières."
        canonical="/confidentialite"
      />
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold text-foreground mb-8">Politique de Confidentialité</h1>
        
        <p className="text-muted-foreground mb-8">
          Dernière mise à jour : 2 janvier 2025
        </p>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
            <p className="text-muted-foreground">
              La protection de vos données personnelles est une priorité pour Immovox. Cette politique de 
              confidentialité vous informe sur la manière dont nous collectons, utilisons et protégeons 
              vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">2. Responsable du traitement</h2>
            <p className="text-muted-foreground mb-4">
              Le responsable du traitement des données personnelles est :<br />
              <strong>KALYVOX SASU</strong><br />
              Siège social : 6 place du Président Thomas Woodrow Wilson, 31000 Toulouse<br />
              RCS Toulouse : 999 458 409<br />
              Email : <a href="mailto:dpo@immovox.fr" className="text-accent hover:underline">dpo@immovox.fr</a>
            </p>
            <p className="text-muted-foreground">
              <strong>Immovox</strong> est une marque commerciale de KALYVOX SASU.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">3. Données collectées</h2>
            <p className="text-muted-foreground mb-4">
              Dans le cadre de notre service de qualification téléphonique IA, nous collectons :
            </p>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li><strong>Données d'identification de l'agence :</strong> nom de l'agence, adresse email, numéro de téléphone professionnel</li>
              <li><strong>Données des utilisateurs :</strong> nom, prénom, adresse email professionnelle des collaborateurs</li>
              <li><strong>Données des appels :</strong> numéro de l'appelant, transcription de la conversation, qualification de l'intention</li>
              <li><strong>Données de connexion :</strong> adresse IP, logs de connexion, type de navigateur</li>
              <li><strong>Données de facturation :</strong> informations de paiement via Stripe (nous ne stockons pas vos données bancaires)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">4. Finalités du traitement</h2>
            <p className="text-muted-foreground mb-4">
              Vos données sont traitées pour les finalités suivantes :
            </p>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li>Fourniture du service de qualification téléphonique automatisé</li>
              <li>Génération des fiches de tickets et synthèses d'appels</li>
              <li>Gestion de la relation client et support technique</li>
              <li>Facturation et gestion des abonnements</li>
              <li>Amélioration de nos algorithmes d'IA (de manière anonymisée)</li>
              <li>Respect de nos obligations légales</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">5. Base légale du traitement</h2>
            <p className="text-muted-foreground">
              Les traitements de données sont fondés sur :<br />
              • <strong>L'exécution du contrat</strong> pour la fourniture du service de qualification téléphonique<br />
              • <strong>Votre consentement</strong> pour les communications marketing<br />
              • <strong>Nos obligations légales</strong> pour la conservation des données de facturation<br />
              • <strong>Notre intérêt légitime</strong> pour l'amélioration de nos services et la sécurité
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">6. Destinataires des données</h2>
            <p className="text-muted-foreground mb-4">
              Vos données peuvent être transmises aux sous-traitants suivants :
            </p>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li><strong>Supabase Inc.</strong> - Hébergement et base de données (Singapour / AWS EU)</li>
              <li><strong>Twilio Inc.</strong> - Téléphonie et numéros virtuels (États-Unis, clauses contractuelles types)</li>
              <li><strong>Stripe Inc.</strong> - Traitement des paiements (États-Unis, certifié Privacy Shield)</li>
              <li><strong>OpenAI / Fournisseurs IA</strong> - Traitement vocal et transcription (avec anonymisation)</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Aucune donnée n'est vendue à des tiers. Les autorités compétentes peuvent y accéder sur demande légale.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">7. Durée de conservation</h2>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li><strong>Données de compte :</strong> durée de l'abonnement + 3 ans après résiliation</li>
              <li><strong>Transcriptions d'appels :</strong> 12 mois glissants (paramétrable par l'agence)</li>
              <li><strong>Données de facturation :</strong> 10 ans (obligation légale comptable)</li>
              <li><strong>Logs de connexion :</strong> 12 mois</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">8. Vos droits</h2>
            <p className="text-muted-foreground mb-4">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="text-muted-foreground list-disc pl-6 space-y-2">
              <li><strong>Droit d'accès :</strong> obtenir une copie de vos données</li>
              <li><strong>Droit de rectification :</strong> corriger vos données inexactes</li>
              <li><strong>Droit à l'effacement :</strong> supprimer vos données ("droit à l'oubli")</li>
              <li><strong>Droit à la portabilité :</strong> récupérer vos données dans un format structuré (JSON/CSV)</li>
              <li><strong>Droit d'opposition :</strong> vous opposer au traitement pour motif légitime</li>
              <li><strong>Droit à la limitation :</strong> geler temporairement le traitement</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Pour exercer ces droits, contactez-nous à : <a href="mailto:dpo@immovox.fr" className="text-accent hover:underline">dpo@immovox.fr</a><br />
              Délai de réponse : 30 jours maximum.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">9. Sécurité des données</h2>
            <p className="text-muted-foreground">
              Nous mettons en œuvre des mesures techniques et organisationnelles robustes :<br />
              • Chiffrement des données au repos (AES-256) et en transit (TLS 1.3)<br />
              • Authentification sécurisée avec hachage des mots de passe<br />
              • Contrôle d'accès basé sur les rôles (RLS - Row Level Security)<br />
              • Hébergement sur infrastructure AWS certifiée ISO 27001<br />
              • Audits de sécurité réguliers et tests de pénétration
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">10. Transferts internationaux</h2>
            <p className="text-muted-foreground">
              Certains de nos sous-traitants sont situés hors UE (États-Unis). Ces transferts sont encadrés par :<br />
              • Les Clauses Contractuelles Types de la Commission Européenne<br />
              • Les certifications Privacy Shield (le cas échéant)<br />
              • Des mesures supplémentaires de sécurité conformes aux recommandations de la CNIL
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">11. Cookies</h2>
            <p className="text-muted-foreground">
              Notre site utilise uniquement des cookies strictement nécessaires au fonctionnement :<br />
              • Cookie d'authentification (session utilisateur)<br />
              • Cookie de préférences (thème, langue)<br />
              <br />
              Aucun cookie publicitaire ou de tracking tiers n'est déposé sans consentement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">12. Modifications</h2>
            <p className="text-muted-foreground">
              Cette politique peut être modifiée pour refléter les évolutions légales ou de nos pratiques. 
              Toute modification substantielle sera notifiée par email aux utilisateurs concernés 
              au moins 30 jours avant son entrée en vigueur.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">13. Contact et réclamations</h2>
            <p className="text-muted-foreground">
              Pour toute question relative à cette politique ou à vos données personnelles :<br />
              Email : <a href="mailto:dpo@immovox.fr" className="text-accent hover:underline">dpo@immovox.fr</a><br />
              Adresse : KALYVOX SASU, 6 place du Président Thomas Woodrow Wilson, 31000 Toulouse
            </p>
            <p className="text-muted-foreground mt-4">
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation 
              auprès de la CNIL : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">www.cnil.fr</a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <a href="/" className="text-accent hover:underline">← Retour à l'accueil</a>
        </div>
      </div>
    </div>
  );
};

export default Confidentialite;